/**
 * Push Notification Helper
 *
 * Utilities for managing browser push notifications
 */

import { notificationService } from '@/services/notification.service';
import { devLog } from '@/utils/logger';

/**
 * Check if push notifications are supported
 */
export const isPushSupported = (): boolean => {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

/**
 * Get current notification permission state
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    devLog.warn('[Push] Notifications not supported');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  devLog.log('[Push] Permission result:', permission);
  return permission;
};

/**
 * Convert VAPID key from base64 to Uint8Array
 * Required for PushSubscription
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Get existing push subscription
 */
export const getExistingSubscription = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    devLog.error('[Push] Error getting existing subscription:', error);
    return null;
  }
};

/**
 * Subscribe to push notifications
 *
 * Steps:
 * 1. Check browser support
 * 2. Request permission
 * 3. Get VAPID key from backend
 * 4. Subscribe via service worker
 * 5. Send subscription to backend
 */
export const subscribeToPushNotifications = async (): Promise<{
  success: boolean;
  error?: string;
  subscription?: PushSubscription;
}> => {
  // Step 1: Check support
  if (!isPushSupported()) {
    return {
      success: false,
      error: 'Push notifications are not supported in this browser.',
    };
  }

  try {
    // Step 2: Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return {
        success: false,
        error: 'Notification permission was denied.',
      };
    }

    // Step 3: Get VAPID key from backend
    const vapidPublicKey = await notificationService.getVapidPublicKey();
    if (!vapidPublicKey) {
      return {
        success: false,
        error: 'Failed to get push notification configuration.',
      };
    }

    // Step 4: Subscribe via service worker
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
      });
    }

    // Step 5: Send subscription to backend
    const subscriptionJson = subscription.toJSON();
    await notificationService.subscribeToPush({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscriptionJson.keys?.p256dh || '',
        auth: subscriptionJson.keys?.auth || '',
      },
    });

    devLog.log('[Push] Successfully subscribed to push notifications');
    return {
      success: true,
      subscription,
    };
  } catch (error) {
    devLog.error('[Push] Error subscribing to push:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to subscribe to push notifications.',
    };
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async (): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const subscription = await getExistingSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      devLog.log('[Push] Successfully unsubscribed from push notifications');
    }

    return { success: true };
  } catch (error) {
    devLog.error('[Push] Error unsubscribing from push:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to unsubscribe from push notifications.',
    };
  }
};

/**
 * Check if user is subscribed to push notifications
 */
export const isSubscribedToPush = async (): Promise<boolean> => {
  const subscription = await getExistingSubscription();
  return subscription !== null;
};

/**
 * Health check: auto-repair expired push subscriptions.
 * If user previously enabled push but the browser subscription expired,
 * silently re-subscribe without user intervention.
 *
 * Should be called once on app load (e.g., in SSEContext or App).
 */
export const checkAndRepairPushSubscription = async (): Promise<void> => {
  // Gate 1: Browser must support push
  if (!isPushSupported()) return;

  // Gate 2: Permission must already be granted (don't prompt)
  if (getNotificationPermission() !== 'granted') return;

  // Gate 3: Check if browser still has a subscription
  const existingSubscription = await getExistingSubscription();
  if (existingSubscription) return; // Healthy - nothing to do

  // Gate 4: Check if user wants push (preferences)
  // Only auto-repair if they previously opted in
  try {
    const preferences = await notificationService.getPreferences();
    if (!preferences.pushEnabled) return; // User disabled push, respect that
  } catch {
    return; // Can't check preferences (not logged in?), skip
  }

  // Subscription is gone but user wants push - re-subscribe silently
  devLog.log('[Push] Subscription expired, auto-repairing...');
  const result = await subscribeToPushNotifications();
  if (result.success) {
    devLog.log('[Push] Auto-repair successful');
  } else {
    devLog.warn('[Push] Auto-repair failed:', result.error);
  }
};
